import { Divider, LineLoader, SimpleList } from '@components'

import { 
  ArrowUpTrayIcon,
  CheckCircleIcon, 
  PaperAirplaneIcon,
  QuestionMarkCircleIcon,
  XCircleIcon, 
} from '@heroicons/react/24/outline'

import DeliverySummaryItem from './DeliverySummaryItem'
import ProcessingEmailsLoader from '../ProcessingEmailsLoader'
import StatsNotReadyPlaceholder from '../StatsNotReadyPlaceholder'
import PropTypes from "prop-types"

const StatsSummary = ({ 
  handleGetDetailedStats,
  handleRefetch,
  hasLoadedOnce,
  isRefetching,
  isLoadingDetailedStats, 
  isProcessing,
  isReady, 
  stats, 
  statuses 
}) => {
  const getStatusIds = (statusFlag) => statuses.filter(status => status[statusFlag]).map(status => status.id)

  if (!isReady) {
    return <StatsNotReadyPlaceholder handleRefetch={handleRefetch} isRefetching={isRefetching} />
  }

  return (
    <div>    
      <div className='mb-4'>
        <h2 className='text-lg font-semibold'>Delivery Summary</h2>
        <p className='text-content2-foreground'>A breakdown of the success or failure of your emails. Click on the relevant view buttons below for further details.</p>
      </div>
      
      <Divider className='mb-4' />

      {!hasLoadedOnce && (
        <LineLoader message="Loading delivery stats..." classNames="mt-8" />
      )}
      
      {hasLoadedOnce && isProcessing && <ProcessingEmailsLoader queuedCount={stats?.summary.queued} />}

      {hasLoadedOnce && (
        <SimpleList>
          <DeliverySummaryItem 
            caption="Delivered" 
            count={stats.summary.delivered} 
            icon={CheckCircleIcon} 
            iconColor="text-green-500" 
            buttonColor="success" 
            handleGetDetailedStats={() => handleGetDetailedStats("is_delivered", getStatusIds("is_delivered"), `Delivered Emails (${stats.summary.delivered})`)}
            isLoading={isLoadingDetailedStats.is_delivered}
          />
          
          <DeliverySummaryItem 
            caption="Failed" 
            count={stats.summary.failed} 
            icon={XCircleIcon} 
            iconColor="text-red-500" 
            buttonColor="danger" 
            handleGetDetailedStats={() => handleGetDetailedStats("is_failed", getStatusIds("is_failed"), `Failed Emails (${stats.summary.failed})`)} 
            isLoading={isLoadingDetailedStats.is_failed}
          />

          <DeliverySummaryItem 
            caption="Unknown" 
            count={stats.summary.unknown} 
            icon={QuestionMarkCircleIcon} 
            iconColor="text-orange-500" 
            buttonColor="warning" 
            handleGetDetailedStats={() => handleGetDetailedStats("is_unknown", getStatusIds("is_unknown"), `Unknown Emails (${stats.summary.unknown})`)} 
            isLoading={isLoadingDetailedStats.is_unknown}
          />
          
          <DeliverySummaryItem 
            caption="Queued" 
            count={stats.summary.queued} 
            icon={ArrowUpTrayIcon} 
            iconColor="text-blue-500" 
            buttonColor="default" 
            handleGetDetailedStats={() => handleGetDetailedStats("is_queued", getStatusIds("is_queued"), `Queued Emails (${stats.summary.queued})`)} 
            isLoading={isLoadingDetailedStats.is_queued}
          />

          <DeliverySummaryItem 
            caption="Total Emails" 
            count={stats.summary.total} 
            icon={PaperAirplaneIcon} 
            iconColor="text-gray-500" 
            buttonColor="default" 
            handleGetDetailedStats={() => handleGetDetailedStats("all", [], `All Emails (${stats.summary.total})`)} 
            isLoading={isLoadingDetailedStats.all}
          />
        </SimpleList>
      )}
    </div>
  )
}

StatsSummary.propTypes = {
  handleGetDetailedStats: PropTypes.func.isRequired,
  handleRefetch: PropTypes.func.isRequired,
  hasLoadedOnce: PropTypes.bool.isRequired,
  isLoadingDetailedStats: PropTypes.object.isRequired,
  isProcessing: PropTypes.bool.isRequired,
  isReady: PropTypes.bool.isRequired,
  isRefetching: PropTypes.bool.isRequired,
  stats: PropTypes.object,
  statuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_delivered: PropTypes.bool,
    is_failed: PropTypes.bool,
    is_unknown: PropTypes.bool,
    is_queued: PropTypes.bool
  })).isRequired
}

export default StatsSummary